/**
 * The Games snapshot's data handoff, client side (ADR-036; the element is
 * written by the snapshot publisher, its format lives in `@fias/public-site-ui`
 * `handoff.ts`).
 *
 * A cold load of a Games page arrives as a static snapshot. The app mounts over
 * it and would otherwise draw loading states until its requests answer, so the
 * snapshot also carries the data it was drawn from. This module reads that
 * element ONCE (and removes it), and hands each query its own data:
 *
 *   - only for the page the snapshot was drawn for (`path` equals the address
 *     the app loaded at), so it can never seed another page;
 *   - only once per query, so a later visit to the page loads fresh data;
 *   - as already-stale data (`GAMES_SNAPSHOT_DATA_UPDATED_AT`), so React Query
 *     paints it at once and refreshes it in the background.
 *
 * Anything malformed is ignored and the page simply loads as it would without
 * a snapshot. Lives in `lib/` because the Games availability store
 * (`games-availability.ts`), which the whole app asks, reads it too.
 */
import {
  GAMES_SNAPSHOT_DATA_ELEMENT_ID,
  GAMES_SNAPSHOT_DATA_VERSION,
  type GameArchePageData,
  type GameDetailData,
  type GameGenrePageData,
  type GamesHomeData,
  type GamesPageData,
} from '@fias/public-site-ui';

/** When the handed-over data counts as fetched: never — refresh it in the background. */
export const GAMES_SNAPSHOT_DATA_UPDATED_AT = 0;

interface Handoff {
  path: string;
  data: GamesPageData;
  taken: boolean;
}

/** `undefined` until the element has been looked for. */
let handoff: Handoff | null | undefined;

function withoutTrailingSlash(path: string): string {
  return path.length > 1 ? path.replace(/\/+$/, '') : path;
}

function isPageData(value: unknown): value is GamesPageData {
  if (!value || typeof value !== 'object') return false;
  const data = value as Record<string, unknown>;
  switch (data.kind) {
    case 'home':
      return !!data.home && Array.isArray((data.home as GamesHomeData).newest);
    case 'genre':
      return (
        typeof data.genreId === 'string' &&
        Array.isArray(data.pages) &&
        data.pages.length > 0 &&
        data.pages.every((p) => Array.isArray((p as GameGenrePageData).games))
      );
    case 'game':
      return typeof data.slug === 'string' && !!(data.detail as GameDetailData)?.game;
    case 'arche':
      return typeof data.key === 'string' && Array.isArray((data.page as GameArchePageData)?.games);
    default:
      return false;
  }
}

function read(): Handoff | null {
  if (handoff !== undefined) return handoff;
  handoff = null;
  if (typeof document === 'undefined') return null;
  const element = document.getElementById(GAMES_SNAPSHOT_DATA_ELEMENT_ID);
  if (!element) return null;
  element.remove();
  try {
    const parsed = JSON.parse(element.textContent ?? '') as Record<string, unknown>;
    if (
      parsed.v === GAMES_SNAPSHOT_DATA_VERSION &&
      typeof parsed.path === 'string' &&
      isPageData(parsed.data)
    ) {
      handoff = { path: withoutTrailingSlash(parsed.path), data: parsed.data, taken: false };
    }
  } catch {
    // Not ours to fix: the page loads as it would with no snapshot.
  }
  return handoff;
}

/** Whether this page load arrived as a Games snapshot (with readable data). */
export function arrivedWithGamesSnapshot(): boolean {
  return read() !== null;
}

type Wanted =
  | { kind: 'home' }
  | { kind: 'genre'; genreId: string }
  | { kind: 'game'; slug: string }
  | { kind: 'arche'; key: string };

function take(wanted: Wanted): GamesPageData | undefined {
  const current = read();
  if (!current || current.taken) return undefined;
  if (typeof window === 'undefined') return undefined;
  if (withoutTrailingSlash(window.location.pathname) !== current.path) return undefined;
  const { data } = current;
  if (data.kind !== wanted.kind) return undefined;
  if (data.kind === 'genre' && wanted.kind === 'genre' && data.genreId !== wanted.genreId) {
    return undefined;
  }
  if (data.kind === 'game' && wanted.kind === 'game' && data.slug !== wanted.slug) {
    return undefined;
  }
  if (data.kind === 'arche' && wanted.kind === 'arche' && data.key !== wanted.key) {
    return undefined;
  }
  current.taken = true;
  return data;
}

/** The home page's data, if this load arrived as the home page's snapshot. */
export function takeGamesHomeSnapshot(): GamesHomeData | undefined {
  const data = take({ kind: 'home' });
  return data?.kind === 'home' ? data.home : undefined;
}

/**
 * A genre's first pages (most popular first, as the snapshot lists them), in
 * the shape of the paged genre query.
 */
export function takeGameGenreSnapshot(
  genreId: string,
): { pages: GameGenrePageData[]; pageParams: number[] } | undefined {
  const data = take({ kind: 'genre', genreId });
  if (data?.kind !== 'genre') return undefined;
  let offset = 0;
  const pageParams = data.pages.map((page) => {
    const at = offset;
    offset += page.games.length;
    return at;
  });
  return { pages: data.pages, pageParams };
}

/** A game page's data, if this load arrived as that page's snapshot. */
export function takeGameDetailSnapshot(slug: string): GameDetailData | undefined {
  const data = take({ kind: 'game', slug });
  return data?.kind === 'game' ? data.detail : undefined;
}

/** An arche's games page's data, if this load arrived as that page's snapshot. */
export function takeGameArcheSnapshot(archeKey: string): GameArchePageData | undefined {
  const data = take({ kind: 'arche', key: archeKey });
  return data?.kind === 'arche' ? data.page : undefined;
}

/** Test hook: forget what was read, so the next call looks for the element again. */
export function __resetGamesSnapshotDataForTests(): void {
  handoff = undefined;
}
